import { useState } from "react";
import { Aperture, Droplets, Film, Palette, SunMoon, Cpu } from "lucide-react";
import { BinaryGrid } from "@/components/BinaryGrid";
import { BeforeAfter } from "@/components/SkinSection";
import { DownloadsHeader } from "@/components/ManualLink";

export const VIOLET = "#c48bff";

const FEATURES = [
  { icon: SunMoon, title: "Tone curve", body: "A film-style tone response: toe, shoulder and contrast that roll the image the way a negative and print do." },
  { icon: Palette, title: "Per-hue colour", body: "Push, shift and saturate each hue on its own, with film-like crossover between neighbours." },
  { icon: Droplets, title: "Casts and bleach bypass", body: "Colour casts plus a bleach bypass for dense, desaturated contrast." },
  { icon: Aperture, title: "9 film looks", body: "Neutral, Basic Punched, 2383, Vision 3, Indie, Soft Silver and Muted, plus two black and white looks, all from hand-graded ACES LUTs. Tweak anything on top." },
  { icon: Film, title: "Full Texture engine", body: "Grain, halation, diffusion and gate weave from KolorKlinic Texture, built in." },
  { icon: Cpu, title: "Colour space aware", body: "Uses the same colour space menu as Texture: Rec.709, ACES and DaVinci Wide Gamut projects. Fully offline." },
];

const DEMOS = [
  { id: "vision3", tab: "Vision 3", before: "/reactor-vision3-before-v1.jpg", after: "/reactor-vision3-after-v1.jpg" },
  { id: "softsilver", tab: "Soft Silver", before: "/reactor-softsilver-before-v1.jpg", after: "/reactor-softsilver-after-v1.jpg" },
  { id: "punch", tab: "Basic Punched", before: "/reactor-punch-before-v1.jpg", after: "/reactor-punch-after-v1.jpg" },
];

function ReactorDemo() {
  const [d, setD] = useState(DEMOS[0]);
  return (
    <div className="space-y-3" data-testid="reactor-demo">
      <div className="flex flex-wrap gap-2" data-testid="reactor-demo-tabs">
        {DEMOS.map((x) => (
          <button key={x.id} onClick={() => setD(x)} className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors"
            style={d.id === x.id ? { borderColor: VIOLET, color: "#000", background: VIOLET } : { borderColor: "#2a2f3d", color: "#9ba3b4" }} data-testid={`reactor-demo-tab-${x.id}`}>{x.tab}</button>
        ))}
      </div>
      <BeforeAfter key={d.id} id="reactor-look" before={d.before} after={d.after} color={VIOLET} label={d.tab} alt={`Rendered with KolorKlinic Reactor, ${d.tab} look`} />
      <p className="font-mono text-[11px] text-[#5c6479]">Graded with the Reactor plugin itself.</p>
    </div>
  );
}

export const ReactorSection = ({ bins, dl, rebuild }) => (
  <section id="reactor" className="scroll-mt-6 card p-6 space-y-6" data-testid="reactor-section">
    <div>
      <div className="chip mb-3" style={{ color: VIOLET, borderColor: VIOLET }}>new · v1.0 · €129 lifetime · €79 for Texture owners</div>
      <h2 className="font-display uppercase text-3xl md:text-4xl leading-none">KolorKlinic <span style={{ color: VIOLET }}>Reactor</span></h2>
      <p className="mt-3 text-sm md:text-base text-[#9ba3b4] max-w-2xl leading-relaxed">
        Film colour look development for DaVinci Resolve: tone, hue, casts and bleach bypass on top of the full Texture engine.
        Download it free; the trial adds a small watermark.
      </p>
    </div>
    <ReactorDemo />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {FEATURES.map(({ icon: Icon, title, body }) => (
        <div key={title} className="demo-card bg-[#0e1013] border border-[#2a2f3d] p-4" data-testid={`reactor-feature-${title.split(" ")[0].toLowerCase()}`}>
          <div className="flex items-center gap-2 mb-1"><Icon size={16} style={{ color: VIOLET }} /><h3 className="font-display uppercase text-base">{title}</h3></div>
          <p className="text-sm text-[#9ba3b4] leading-relaxed">{body}</p>
        </div>
      ))}
    </div>
    <div>
      <DownloadsHeader slug="Reactor" name="Reactor" color={VIOLET} testid="reactor-manual-download-btn" />
      <BinaryGrid prefix="reactor-" bins={bins} dl={dl} rebuild={rebuild} />
    </div>
  </section>
);
